import type { Address } from "viem"
import { useState } from "react"
import { getAddress } from "viem"
import { observer } from "mobx-react-lite"

import { useScaleApplication } from "@/domains/application/use-scale-application"
import { LcAddress } from "@/domains/core"
import { getTokenMetadata } from "@/domains/on-chain"
import { SwapTokenDialog } from "@/domains/tokens/components/token-dialog/swap-token-dialog"
import { TokenStore } from "@/domains/tokens"
import { useNativeTokenStore } from "@/hooks/use-native-token-store"
import type { SwapFallbackMode } from "@/domains/swap-aggregators/types"

import type { ISwapExecutionStore, ISwapInputStore, ISwapQuoteStore } from "../../interfaces"
import type { NormalizedSwapQuote } from "../../types"
import { getSwapActionMode } from "../../swap-mode"
import type { SwapAggregator } from "../composables/swap-settings-sections/swap-aggregators-section"
import { SwapSettingsDialog } from "../swap-settings-dialog"
import type { SwapRouteDTO, SwapTokenPanelDTO } from "../views/swap-card-container-view"
import { SwapCardContainerView } from "../views/swap-card-container-view"

const LOCAL_TOKENS_KEY = "base-swap-local-token-stores"

export interface SwapCardContainerProps {
  quoteStore: ISwapQuoteStore
  inputStore: ISwapInputStore
  executionStore: ISwapExecutionStore
  onSwapAction: () => void
  slippageTolerance: string
}

export const SwapCardContainer = observer(
  ({ quoteStore, inputStore, executionStore, onSwapAction, slippageTolerance }: SwapCardContainerProps) => {
    const scaleApp = useScaleApplication()
    const nativeTokenStore = useNativeTokenStore()
    const [isInputTokenDialogOpen, setIsInputTokenDialogOpen] = useState(false)
    const [isOutputTokenDialogOpen, setIsOutputTokenDialogOpen] = useState(false)
    const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false)
    const [localTokens, setLocalTokens] = useState<TokenStore[]>(() => loadLocalTokens())

    const actionMode = getSwapActionMode(
      inputStore.inputToken,
      inputStore.outputToken,
      nativeTokenStore,
      scaleApp.Tokens.wrappedNative,
    )
    const routeDecision = quoteStore.routeDecision
    const route = createRouteDto(routeDecision.quote, routeDecision.reason, slippageTolerance)
    const routeUnavailableLabel = createRouteUnavailableLabel(inputStore.inputAmount, route, quoteStore.isLoadingQuotes)
    const inputValueLabel = createValueLabel(inputStore.inputAmount, inputStore.inputToken)

    const aggregatorsForSettings: Array<SwapAggregator> =
      scaleApp.Settings.SwapAggregators.getProductionReadyAggregators().map((agg) => ({
        id: agg.id,
        name: agg.name,
        description: agg.description,
        icon: agg.icon,
        enabled: agg.id === scaleApp.Settings.SwapAggregators.activeFallbackAggregatorId,
      }))

    const handleFallbackAggregatorSelect = (aggregatorId: string): void => {
      scaleApp.Settings.SwapAggregators.setActiveFallbackAggregator(aggregatorId)
      scaleApp.Swap.InputsOrchestrator.handleTokenChange()
    }

    const handleFallbackModeChange = (mode: SwapFallbackMode): void => {
      scaleApp.Settings.SwapAggregators.setFallbackMode(mode)
      scaleApp.Swap.InputsOrchestrator.handleTokenChange()
    }

    const handleSlippageChanged = (slippage: string): void => {
      const slippageNumber = Number.parseFloat(slippage)
      if (Number.isNaN(slippageNumber)) return

      scaleApp.Settings.setSlippageFromUi(slippageNumber)
      scaleApp.Swap.InputsOrchestrator.handleTokenChange()
    }

    const handleSwapDone = (): void => {
      executionStore.reset()
      inputStore.setInputAmount("")
      quoteStore.clearAggregator()
    }

    const handleLocalTokenAdd = async (address: string): Promise<void> => {
      const checksummedAddress = getAddress(address)
      const existingToken = localTokens.find((token) => token.address.equalsString(checksummedAddress))
      if (existingToken) {
        return
      }

      const metadata = await getTokenMetadata({
        tokenAddress: checksummedAddress,
        config: scaleApp.getWagmiConfig(),
        chainId: scaleApp.Config.chainId,
      })
      if (!metadata) {
        throw new Error("Token metadata could not be loaded")
      }

      const token = new TokenStore(
        {
          address: new LcAddress(metadata.address),
          symbol: metadata.symbol,
          name: metadata.name,
          decimals: metadata.decimals,
          isWhitelisted: false,
          isSwapToken: false,
          isIncentiveToken: false,
          tags: [],
          priceUsd: "0",
        },
        "user",
        "token-api",
      )
      const nextTokens = [...localTokens, token]
      setLocalTokens(nextTokens)
      saveLocalTokens(nextTokens)
    }

    const handleLocalTokenRemove = (address: Address): void => {
      const nextTokens = localTokens.filter((token) => !token.address.equalsString(address))
      setLocalTokens(nextTokens)
      saveLocalTokens(nextTokens)
    }

    return (
      <>
        <SwapCardContainerView
          inputToken={createTokenPanelDto(inputStore.inputToken)}
          outputToken={createTokenPanelDto(inputStore.outputToken)}
          actionMode={actionMode}
          inputAmount={inputStore.inputAmount}
          inputValueLabel={inputValueLabel}
          route={route}
          routeUnavailableLabel={routeUnavailableLabel}
          isLoadingQuotes={quoteStore.isLoadingQuotes}
          onTypingChange={(amount) => {
            inputStore.setInputAmount(amount)
            scaleApp.Swap.InputsOrchestrator.handleInputAmountChange(amount, "typing")
          }}
          onBalanceClick={(amount) => {
            inputStore.setInputAmount(amount)
            scaleApp.Swap.InputsOrchestrator.handleInputAmountChange(amount, "balance-click")
          }}
          onPercentageButtonClick={(percentage) => {
            if (!inputStore.inputToken) return
            const amount = inputStore.inputToken.calculateValuePercentage(inputStore.inputToken.balance, percentage / 100)
            inputStore.setInputAmount(amount)
            scaleApp.Swap.InputsOrchestrator.handleInputAmountChange(amount, "button")
          }}
          onInputTokenSelect={() => setIsInputTokenDialogOpen(true)}
          onOutputTokenSelect={() => setIsOutputTokenDialogOpen(true)}
          onSwitchTokens={() => {
            inputStore.switchTokens()
            scaleApp.Swap.InputsOrchestrator.handleTokenChange()
          }}
          onRefreshQuotes={() => scaleApp.Swap.InputsOrchestrator.handleTokenChange()}
          isSwappingProcessActive={
            executionStore.state === "approving" ||
            executionStore.state === "swapping" ||
            executionStore.state === "success" ||
            executionStore.state === "error"
          }
          isWalletConnected={scaleApp.Wallet.Connection.isWalletConnected}
          onSwapAction={onSwapAction}
          onSwapDone={handleSwapDone}
          hasError={executionStore.state === "error"}
          isSuccess={executionStore.state === "success"}
          errorMessage={executionStore.error}
          onSettingsClick={() => setIsSettingsDialogOpen(true)}
          slippageTolerance={slippageTolerance}
        />

        <SwapTokenDialog
          isOpen={isInputTokenDialogOpen}
          tokens={scaleApp.Tokens.Repository.getAll()}
          quickSelectTokens={scaleApp.Tokens.QuickSelect.tokens}
          governanceToken={{ name: scaleApp.Tokens.governance.name }}
          nativeToken={nativeTokenStore}
          showLocalAssets={true}
          localTokens={localTokens}
          onAddLocalToken={handleLocalTokenAdd}
          onRemoveLocalToken={handleLocalTokenRemove}
          wrappedNativeToken={scaleApp.Tokens.wrappedNative}
          onClose={() => setIsInputTokenDialogOpen(false)}
          onTokenSelect={(token) => {
            selectInputToken(token, inputStore)
            scaleApp.Swap.InputsOrchestrator.handleTokenChange()
            setIsInputTokenDialogOpen(false)
          }}
        />

        <SwapTokenDialog
          isOpen={isOutputTokenDialogOpen}
          tokens={scaleApp.Tokens.Repository.getAll()}
          quickSelectTokens={scaleApp.Tokens.QuickSelect.tokens}
          governanceToken={{ name: scaleApp.Tokens.governance.name }}
          nativeToken={nativeTokenStore}
          showLocalAssets={true}
          localTokens={localTokens}
          onAddLocalToken={handleLocalTokenAdd}
          onRemoveLocalToken={handleLocalTokenRemove}
          wrappedNativeToken={scaleApp.Tokens.wrappedNative}
          onClose={() => setIsOutputTokenDialogOpen(false)}
          onTokenSelect={(token) => {
            selectOutputToken(token, inputStore)
            scaleApp.Swap.InputsOrchestrator.handleTokenChange()
            setIsOutputTokenDialogOpen(false)
          }}
        />

        <SwapSettingsDialog
          isOpen={isSettingsDialogOpen}
          onClose={() => setIsSettingsDialogOpen(false)}
          aggregators={aggregatorsForSettings}
          fallbackMode={scaleApp.Settings.SwapAggregators.fallbackMode}
          slippage={`${scaleApp.Settings.slippage}`}
          onFallbackAggregatorSelect={handleFallbackAggregatorSelect}
          onFallbackModeChange={handleFallbackModeChange}
          onSlippageChanged={handleSlippageChanged}
        />
      </>
    )
  },
)

const selectInputToken = (token: TokenStore | null, inputStore: ISwapInputStore): void => {
  if (!token) return
  if (inputStore.outputToken && token.address.equals(inputStore.outputToken.address)) {
    inputStore.switchTokens()
    return
  }
  inputStore.setInputToken(token)
}

const selectOutputToken = (token: TokenStore | null, inputStore: ISwapInputStore): void => {
  if (!token) return
  if (inputStore.inputToken && token.address.equals(inputStore.inputToken.address)) {
    inputStore.switchTokens()
    return
  }
  inputStore.setOutputToken(token)
}

const createTokenPanelDto = (token: TokenStore | undefined): SwapTokenPanelDTO | undefined => {
  if (!token) return undefined

  return {
    symbol: token.symbol,
    name: token.name,
    imageUrl: token.imageURL,
    balanceLabel: trimTokenAmount(token.displayBalance),
    maxAmountLabel: token.displayBalance,
    valueLabel: createValueLabel(token.displayBalance, token),
  }
}

const createRouteDto = (
  quote: NormalizedSwapQuote | undefined,
  reason: string,
  slippageTolerance: string,
): SwapRouteDTO | undefined => {
  if (!quote) return undefined

  const route = Array.isArray(quote.transactionMetadata.route) ? quote.transactionMetadata.route : []
  const isFallback = quote.aggregatorId !== "scale"
  const isUnsafe = reason.includes("too-high") || reason.includes("unknown")

  return {
    aggregatorId: quote.aggregatorId,
    sourceLabel: isFallback ? formatProviderLabel(quote.aggregatorId) : "Scale",
    statusLabel: "Quote ready",
    reasonLabel: getRouteReasonLabel(reason, quote.aggregatorId),
    outputAmount: trimTokenAmount(quote.outputAmountFormatted),
    outputValueLabel: createValueLabel(quote.outputAmountFormatted, quote.outputToken),
    priceImpactLabel: quote.priceImpactBps === null ? "Unknown" : `${(quote.priceImpactBps / 100).toFixed(2)}%`,
    hopLabel: route.length > 0 ? `${route.length} hop${route.length === 1 ? "" : "s"}` : "External",
    slippageLabel: slippageTolerance,
    isFallback,
    isUnsafe,
  }
}

const createRouteUnavailableLabel = (
  inputAmount: string,
  route: SwapRouteDTO | undefined,
  isLoadingQuotes: boolean,
): string | undefined => {
  if (!inputAmount || Number(inputAmount) <= 0 || route || isLoadingQuotes) {
    return undefined
  }

  return "No quote is available right now. Try refreshing, adjusting the amount, or changing swap settings."
}

const getRouteReasonLabel = (reason: string, _aggregatorId: string): string => {
  switch (reason) {
    case "scale-route-healthy":
      return "Best available quote found."
    case "external-meaningfully-better":
      return "Best available quote found."
    case "external-fallback":
      return "Best available quote found."
    default:
      return "Best available quote found."
  }
}

const formatProviderLabel = (aggregatorId: string): string => {
  switch (aggregatorId) {
    case "fly":
      return "Fly"
    case "kyber":
      return "KyberSwap"
    case "openocean":
      return "OpenOcean"
    default:
      return aggregatorId
  }
}

const createValueLabel = (amount: string, token: TokenStore | undefined): string => {
  if (!token || !amount || Number.isNaN(Number(amount))) {
    return "$0.00"
  }

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: "compact",
    maximumFractionDigits: 2,
  }).format(Number(amount) * token.bestPrice)
}

const trimTokenAmount = (amount: string): string => {
  const numericAmount = Number(amount)
  if (!Number.isFinite(numericAmount)) return amount
  return numericAmount.toLocaleString("en-US", {
    maximumFractionDigits: numericAmount < 1 ? 8 : 6,
  })
}

const loadLocalTokens = (): TokenStore[] => {
  try {
    const rawTokens = JSON.parse(localStorage.getItem(LOCAL_TOKENS_KEY) ?? "[]") as Array<{
      address: string
      symbol: string
      name: string
      decimals: number
    }>
    return rawTokens.map(
      (token) =>
        new TokenStore(
          {
            address: new LcAddress(token.address),
            symbol: token.symbol,
            name: token.name,
            decimals: token.decimals,
            isWhitelisted: false,
            isSwapToken: false,
            isIncentiveToken: false,
            tags: [],
            priceUsd: "0",
          },
          "user",
          "token-api",
        ),
    )
  } catch {
    return []
  }
}

const saveLocalTokens = (tokens: TokenStore[]): void => {
  localStorage.setItem(
    LOCAL_TOKENS_KEY,
    JSON.stringify(
      tokens.map((token) => ({
        address: token.address.toString(),
        symbol: token.symbol,
        name: token.name,
        decimals: token.decimals,
      })),
    ),
  )
}
